import { useMemo, useState } from "react";
import { topicTags } from "@/mocks/homeContent";
import { useReveal } from "@/hooks/useReveal";

type TopicTag = (typeof topicTags)[number];

const toneClass: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800 hover:border-primary-400",
  accent: "bg-accent-100 text-accent-800 hover:border-accent-400",
  secondary: "bg-secondary-100 text-secondary-800 hover:border-secondary-400",
};

const sizeClass: Record<string, string> = {
  lg: "text-lg md:text-xl px-5 py-3 font-heading font-semibold",
  md: "text-base md:text-lg px-4 py-2.5 font-heading font-medium",
  sm: "text-sm md:text-base px-3.5 py-2",
};

export default function TopicWall() {
  const [activeTag, setActiveTag] = useState("g1");
  const { ref, visible } = useReveal<HTMLDivElement>(0.1);

  const active = useMemo<TopicTag>(
    () => topicTags.find((tag) => tag.id === activeTag) ?? topicTags[0],
    [activeTag]
  );

  return (
    <section id="topics" className="w-full bg-background-100 py-14 md:py-20">
      <div
        ref={ref}
        className={`mx-auto w-full px-4 md:px-8 ${visible ? "reveal is-visible" : "reveal"}`}
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <span className="text-xs font-medium tracking-[0.18em] text-secondary-600">
                TOPIC WALL
              </span>
            </div>
            <h2 className="font-heading mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">
              话题墙
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              标签即入口。话题墙把内容按题材与形态两个维度归档，点击任意标签即可进入该话题下的全部整理条目。
            </p>

            <div className="mt-6 rounded-lg border border-background-300 bg-background-50 p-5">
              <p className="text-xs text-foreground-500">当前选中话题</p>
              <p className="font-heading mt-2 text-xl font-bold text-foreground-950">
                {active.label}
              </p>
              <div className="mt-3 flex items-center gap-4 text-xs text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-2-line text-primary-600" /> {active.count} 条内容
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line text-accent-700" /> 持续更新
                </span>
              </div>
              <button
                type="button"
                className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                进入该话题
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-background-400 bg-background-50 p-5 md:p-6">
              {topicTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => setActiveTag(tag.id)}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-full border border-transparent transition-all ${
                    sizeClass[tag.size]
                  } ${toneClass[tag.tone]} ${
                    activeTag === tag.id ? "ring-2 ring-primary-400 ring-offset-2 ring-offset-background-50" : ""
                  }`}
                >
                  {tag.label}
                  <span className="text-xs font-normal opacity-70">{tag.count}</span>
                </button>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-background-300 bg-background-50 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-price-tag-3-line" />
                </span>
                <p className="mt-3 font-heading text-lg font-semibold text-foreground-950">12 个题材</p>
                <p className="mt-1 text-xs text-foreground-600">覆盖剧组、综艺、版权与合约等方向</p>
              </div>
              <div className="rounded-lg border border-background-300 bg-background-50 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-node-tree" />
                </span>
                <p className="mt-3 font-heading text-lg font-semibold text-foreground-950">4 种形态</p>
                <p className="mt-1 text-xs text-foreground-600">快讯、时间线、深度长文与档案整理</p>
              </div>
              <div className="rounded-lg border border-background-300 bg-background-50 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-discuss-line" />
                </span>
                <p className="mt-3 font-heading text-lg font-semibold text-foreground-950">讨论氛围</p>
                <p className="mt-1 text-xs text-foreground-600">每个话题下都有实时评论与补充线索</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}